import React from 'react';
import { Box, Typography, Chip, /* Button, */ TextField, InputAdornment, Alert } from '@mui/material'; // Button: table transfer disabled
import {
    Search as SearchIcon,
    // SwapHoriz as TransferIcon, // table transfer disabled
    Person as PersonIcon,
    EventSeat as SeatIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import TableTimer from './TableTimer';

const matchesSearch = (table, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [table.tableName, table.tableNumber, table.location, table.activeDealer?.dealer?.name]
        .some((value) => value && String(value).toLowerCase().includes(q));
};

// Table picker shown to a dealer before opening a single table. Tables another
// dealer is currently running are shown locked and cannot be opened.
const DealerTableGrid = ({ tables, currentUserId, search, onSearchChange, onOpenTable /* , onTransferTable */ }) => {
    const visibleTables = tables.filter((table) => matchesSearch(table, search.trim()));

    return (
        <Box>
            <TextField
                fullWidth
                size="small"
                placeholder="Search by table name, number, dealer or location..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        color: 'white',
                        bgcolor: 'rgba(15,23,42,0.6)',
                        borderRadius: 2,
                        '& fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                        '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.6)' },
                        '&.Mui-focused fieldset': { borderColor: 'var(--cm-warning)' },
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />
                        </InputAdornment>
                    ),
                }}
            />

            {tables.length === 0 ? (
                <Alert severity="info">No tables are assigned to you</Alert>
            ) : visibleTables.length === 0 ? (
                <Alert severity="info">No tables match "{search}"</Alert>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
                    {visibleTables.map((table) => {
                        const isRunning = Boolean(table.runningSince);
                        const holder = table.activeDealer?.dealer;
                        // Tables this dealer transferred away stay listed but can't be opened again.
                        const dealerName = holder?.name || (table.transferredOut && table.transferredToName) || 'No dealer';
                        const isLocked = table.transferredOut
                            || (Boolean(holder) && String(holder._id) !== String(currentUserId));
                        const handleOpen = () => {
                            if (isLocked) {
                                toast.info(table.transferredOut
                                    ? `You transferred ${table.tableName} to ${dealerName}`
                                    : `${dealerName} is running ${table.tableName}`);
                                return;
                            }
                            onOpenTable(table);
                        };

                        return (
                            <Box
                                key={table._id}
                                className="table-card"
                                role="button"
                                tabIndex={0}
                                aria-disabled={isLocked}
                                onClick={handleOpen}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleOpen();
                                    }
                                }}
                                sx={{
                                    cursor: isLocked ? 'not-allowed' : 'pointer',
                                    opacity: isLocked ? 0.6 : 1,
                                    filter: isLocked ? 'grayscale(0.5)' : 'none',
                                    borderRadius: 3,
                                    p: 1.75,
                                    color: 'white',
                                    background: 'radial-gradient(ellipse at top left, #168047 0%, #086132 60%, #064626 100%)',
                                    border: '3px solid #6b3f22',
                                    boxShadow: 'inset 0 0 0 1px #9a6339, 0 8px 18px rgba(0,0,0,0.4)',
                                    transition: 'transform 0.15s, box-shadow 0.15s',
                                    '&:hover, &:focus-visible': isLocked ? { outline: 'none' } : {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 'inset 0 0 0 1px #9a6339, 0 12px 24px rgba(0,0,0,0.5)',
                                        outline: 'none',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                                            Table {table.tableNumber}
                                        </Typography>
                                        <Typography noWrap sx={{ fontSize: 18, fontWeight: 800 }}>
                                            {table.tableName}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        icon={isLocked && !table.transferredOut ? <LockIcon sx={{ fontSize: 14, color: 'white !important' }} /> : undefined}
                                        label={table.transferredOut ? 'Active' : isLocked ? 'In use' : isRunning ? 'Running' : 'Not running'}
                                        size="small"
                                        sx={{
                                            flexShrink: 0,
                                            bgcolor: table.transferredOut ? '#22c55e' : isLocked ? '#dc2626' : isRunning ? '#22c55e' : 'rgba(15,23,42,0.65)',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '0.65rem',
                                            height: 22,
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                                    <PersonIcon sx={{ fontSize: 16, color: '#fde68a' }} />
                                    <Typography noWrap sx={{ color: '#fde68a', fontSize: 13, fontWeight: 700 }}>
                                        Dealer: {dealerName}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, mb: 1 }}>
                                    <SeatIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }} />
                                    <Typography noWrap sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
                                        {table.occupiedSeats}/{table.totalSeats} players
                                        {table.location ? ` · ${table.location}` : ''}
                                    </Typography>
                                </Box>

                                <TableTimer
                                    runningSince={table.runningSince}
                                    lastStoppedAt={table.lastStoppedAt}
                                    lastRunMinutes={table.lastRunMinutes}
                                />

                                {/* Table transfer disabled: not needed for now
                                {!isLocked && (
                                    <Button
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        startIcon={<TransferIcon />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onTransferTable(table);
                                        }}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        sx={{
                                            color: 'white',
                                            borderColor: 'rgba(255,255,255,0.4)',
                                            bgcolor: 'rgba(2,44,27,0.5)',
                                            fontWeight: 700,
                                            '&:hover': { borderColor: 'var(--cm-warning)', bgcolor: 'rgba(245,158,11,0.2)' },
                                        }}
                                    >
                                        Transfer
                                    </Button>
                                )}
                                */}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default DealerTableGrid;
